<script lang="ts">
	// Stores
	import { devicesFilters } from 'src/stores/ui/filters';
	import { edit, bulkActionEdit } from 'src/stores/ui/edit_lists';

	import { createListHeaderHandle, type ListHeaderConfig } from 'src/stores/list_header';
	// Design system components
	import { BreadcrumbsFlexWrapper, Toolbar } from '@Shelly/shelly-design-system';
	// Components
	import { createEventDispatcher, onDestroy } from 'svelte';
	import AddEditButtonsNew from '../../AddEditButtonsNew.svelte';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';
	import EditRoomWizard from './EditRoomWizard/EditRoomWizard.svelte';
	import RoomActionsAdapter from './RoomActionsAdapter.svelte';

	// Group this imports
	import { querystring, replace, location } from 'svelte-spa-router';
	import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	import DeleteRoomModal from './DeleteRoomModal/DeleteRoomModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import DeviceListToggleLayout from '../Devices/DeviceListToggleLayout.svelte';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import { getSharedHomeManagedTooltip } from 'src/helpers/houses/shared_home_messaging_helpers';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';

	export let id: string;
	export let type;
	export let list;
	export let currentRoute;
	export let showBulkActions: boolean = false;
	export let itemsLength: number = 0;
	export let showDeviceListToggle: boolean = false;

	const dispatch = createEventDispatcher<{ 'select-all': {}; 'submit-click': {} }>();
	const headerHandle = createListHeaderHandle();

	$: emptyList = itemsLength === 0;
	$: isSharedHomeActive = isActiveHomeShared($homes.list);

	let showEditRoomModal = false;
	let showDeleteRoomModal = false;
	let showSharedManagedModal = false;

	const routeFilter = Number($querystring.split('=')[1]);

	if (routeFilter) {
		Object.values($devicesFilters.appliance_types).map((filter) => {
			if (filter.id === routeFilter) {
				filter.selected = true;
			} else {
				filter.selected = false;
			}
			return filter;
		});
		replace(`${$location}`);
	}

	// -------- new-layout publisher --------
	// The room kebab (edit/delete room, reorder, bulk) already lives in
	// `AddEditButtonsNew`. Rather than reproducing that state machine as
	// data-driven menu items, we mount the whole component in `extraActions`.
	// The in-room tab switcher (`RoomToolbar`) and back-to-rooms breadcrumb
	// render below in the template as a slim nav row (not shared config).
	$: if ($showNewLayout) {
		const config: ListHeaderConfig = {
			extraActions: [
				{
					key: 'room-actions',
					component: RoomActionsAdapter,
					props: {
						showBulkActions,
						showDeviceListToggle,
						disableEditButton: emptyList,
						disableReorderButton: itemsLength <= 1,
						managed: isSharedHomeActive,
						sharedHomeManagedTooltip: getSharedHomeManagedTooltip('change_view'),
						onEditRoomClick: () => {
							showEditRoomModal = true;
						},
						onDeleteRoomClick: () => {
							showDeleteRoomModal = true;
						},
						onManagedClick: () => (showSharedManagedModal = true),
						onAddClick: () => edit.set(false),
						onSelectAll: () => dispatch('select-all'),
						onSubmitClick: () => dispatch('submit-click'),
					},
				},
			],
		};
		console.log('[listHeader] publish RoomToolbarWrapper', {
			showBulkActions,
			showDeviceListToggle,
			emptyList,
			itemsLength,
		});
		headerHandle.set(config);
	}

	onDestroy(() => {
		console.log('[listHeader] clear RoomToolbarWrapper');
		headerHandle.clear();
	});
</script>

{#if $showNewLayout}
	<!-- New-layout in-room header: the in-room tab switcher
		 (Devices/Groups/Scenes/Thermostats) is wrapped in a Toolbar so it
		 matches the other lists visually. The back-to-rooms breadcrumb is
		 hidden for now (top-level tabs already surface Rooms). -->
	<Toolbar class="mb-m" isSticky useNewLayout noBreadcrumbs hideSlotRightSide>
		<svelte:fragment slot="leftSide">
			<!-- Room name/back navigation intentionally hidden for now; the
				 outer Tabs already carry the primary Rooms context. -->
			<!-- <RoomBreadCrumbs {id} /> -->
			<!-- Wrap the tokens in a horizontal flex row so the Toolbar's mobile
				 rule that flips `.left-controls-container` to `flex-direction: column`
				 doesn't turn each Token into a full-width stacked item. The wrapper
				 sizes to its content and keeps the tokens on a single row. -->
			<div class="d-flex gap-s align-items-center max-width-fit-content">
				<RoomToolbar />
			</div>
		</svelte:fragment>
	</Toolbar>
{:else}
	{#key showEditRoomModal}
		<Toolbar
			class="mb-m"
			hideSlotBottom={!$is_tablet || $is_mobile}
			hideSlotRightSide={$is_mobile}
			hideSlotLeftSide={!($is_mobile || (!$is_mobile && !$is_tablet))}
			isSticky
		>
			<BreadcrumbsFlexWrapper slot="breadcrumbs" gap="s" hideSlotRight={!$is_mobile}>
				<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
					<RoomBreadCrumbs {id} />
				</div>

				<div slot="right" class="d-flex align-items-center gap-s">
					<AddEditButtonsNew
						{showBulkActions}
						managed={isSharedHomeActive}
						on:managed_click={() => (showSharedManagedModal = true)}
						on:add_click={async () => {
							edit.set(false);
						}}
						on:edit_room_click={() => {
							showEditRoomModal = true;
						}}
						on:delete_room_click={() => {
							showDeleteRoomModal = true;
						}}
						disableEditButton={emptyList}
						disableReorderButton={itemsLength <= 1}
						on:select-all
						on:submit-click
					/>
				</div>
			</BreadcrumbsFlexWrapper>

			<svelte:fragment slot="leftSide">
				<div class="d-flex gap-s max-width-fit-content align-items-center">
					<RoomToolbar />
				</div>
			</svelte:fragment>

			<svelte:fragment slot="rightSide">
				<AddEditButtonsNew
					{showBulkActions}
					managed={isSharedHomeActive}
					on:managed_click={() => (showSharedManagedModal = true)}
					on:add_click={async () => {
						edit.set(false);
					}}
					on:edit_room_click={() => {
						showEditRoomModal = true;
					}}
					on:delete_room_click={() => {
						showDeleteRoomModal = true;
					}}
					disableEditButton={emptyList}
					disableReorderButton={itemsLength <= 1}
					on:select-all
					on:submit-click
				/>
			</svelte:fragment>
		</Toolbar>
	{/key}
{/if}

<EditRoomWizard bind:showEditRoomModal {id} {list} {currentRoute} {type} />
<DeleteRoomModal bind:showDeleteRoomModal {id} {list} {currentRoute} {type} />

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="edit_entity" />
